import React, { useState } from 'react';
import { Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FaTrash } from 'react-icons/fa';
import AddGemstoneForm from './AddGemstoneForm'; // Import AddGemstoneForm
import './FormStyles.css'; // Import the CSS file

const validationSchema = Yup.object({
  size: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Size is required'),
  origin: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Origin is required'),
  metal: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Metal is required'),
  purchasePrice: Yup.number().positive('Purchase price must be positive').required('Purchase price is required'),
  marketPrice: Yup.number().positive('Market price must be positive').required('Market price is required'),
  sellingPrice: Yup.number().positive('Selling price must be positive').required('Selling price is required'),
  stockQuantity: Yup.number().positive('Quantity must be positive').required('Stock quantity is required'),
  rfidNo: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('RFID number is required'),
  color: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Color is required'),
  weight: Yup.number().positive('Weight must be positive').required('Weight is required'),
  purity: Yup.number().positive('Purity must be positive').required('Dimensions are required'),
  items: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Gemstone name is required'),
  photos: Yup.array().min(1, 'At least one photo is required/less then 200KB').max(4, 'Maximum 4 photos are allowed'),
  number: Yup.string().optional(),
  group: Yup.string().optional(),
  description: Yup.string().optional(),
  altcode: Yup.string().optional(),
});

const WatchForm = ({ handleSave, handleClose }) => {
  const initialValues = {
    items: '',
    purity: '',
    origin: '',
    metal: '',
    size: '',
    otheritems: [],
    purchasePrice: '',
    marketPrice: '',
    sellingPrice: '',
    stockQuantity: '',
    weight: '',
    photos: [],
    rfidNo: '',
    color: '',
    number: '',
    group: '',
    description: '',
    altcode: '',
  };
  const [gemstones, setGemstones] = useState([]);
  const [showGemstoneModal, setShowGemstoneModal] = useState(false); // Track gemstone modal visibility
  const [tag, setTag] = useState("");

  const apiUrl = 'https://jwapi.infosware-test.in';

  const handleDeleteGemstone = (gemstoneName, setFieldValue, values) => {
    const updatedGemstones = gemstones.filter((name) => name !== gemstoneName);
    setGemstones(updatedGemstones);

    const updatedOtherItems = values.otheritems.filter(
      (item) => !(item.type === 'gemstone' && item.gemstoneName === gemstoneName)
    );
    setFieldValue('otheritems', updatedOtherItems);
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append('items', values.items);
    formData.append('purity', values.purity);
    formData.append('origin', values.origin);
    formData.append('metal', values.metal);
    formData.append('size', values.size);
    formData.append('otheritems', JSON.stringify(values.otheritems));
    formData.append('purchasePrice', values.purchasePrice);
    formData.append('marketPrice', values.marketPrice);
    formData.append('sellingPrice', values.sellingPrice);
    formData.append('stockQuantity', values.stockQuantity);
    formData.append('rfidNo', values.rfidNo);
    formData.append('color', values.color);
    formData.append('weight', values.weight);
    formData.append('number', values.number);
    formData.append('group', values.group);
    formData.append('description', values.description);
    formData.append('altcode', values.altcode);
    values.photos.forEach(photo => {
      formData.append('photos', photo);
    });

    try {
      const response = await fetch(`${apiUrl}/api/watch`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        alert('Form submitted successfully!');
      } else {
        alert('Failed to submit form.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    }

    handleSave(values);
    handleClose();
  };

  const fetchTagID = async (setFieldValue) => {
    try {
      const response = await fetch(`${apiUrl}/api/read-tag`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setTag(data.tag);
      setFieldValue("rfidNo", data.tag);
    } catch (error) {
      console.error("Error fetching tag ID:", error);
    }
  };

  const handlePhotoChange = (e, setFieldValue, values) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) =>
      ["image/jpeg", "image/png"].includes(file.type) && file.size <= 200 * 1024
    );

    if (!values.photos) {
      values.photos = [];
    }

    const updatedFiles = validFiles.slice(
      0,
      4 - values.photos.length
    );

    setFieldValue("fileObjects", [
      ...(values.fileObjects || []),
      ...updatedFiles,
    ]);
    setFieldValue("photos", [
      ...values.photos,
      ...updatedFiles,
    ]);
  };

  const handleGemstoneSubmit = (gemstone) => {
    setGemstones([...gemstones, gemstone]);
    setShowGemstoneModal(false); // Close the modal after adding the gemstone
  };

  return (
    <div className="form-container">
      <Formik
        initialValues={initialValues}
         validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <FormikForm>
            <div className="form-header">
              <h4 className="mt-3">Add New watch</h4>
              <Button variant="" className="close-button" onClick={handleClose}>
                &times;
              </Button>
            </div>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formnumber">
                  <Form.Label>Number</Form.Label>
                  <Field type="number" name="number" className="form-control" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formGroup">
                  <Form.Label>Group</Form.Label>
                  <Field as="select" name="group" className="form-control">
                    <option value="">Select Group</option>
                    <option value="group1">Group 1</option>
                    <option value="group2">Group 2</option>
                    <option value="group3">Group 3</option>
                  </Field>
                </Form.Group>
              </Col>
            </Row>

            {/* Description and AltCode */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formDescription">
                  <Form.Label>Description</Form.Label>
                  <Field type="text" name="description" className="form-control" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formAltCode">
                  <Form.Label>Alt Code</Form.Label>
                  <Field type="text" name="altcode" className="form-control" />
                </Form.Group>
              </Col>
            </Row>

            {/* Description and AltCode */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formGemstoneName">
                  <Form.Label>Watch Name</Form.Label>
                  <Field type="text" name="items" className="form-control" />
                  <ErrorMessage name="items" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formgemstone">
                  <Form.Label>Gemstone</Form.Label>
                  <Field
                    as="select"
                    name="otheritems"
                    className="form-control"
                    value="" // Ensure the value is a scalar
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setFieldValue('gemstone', selectedValue); // Update the gemstone field when option is selected
                      if (selectedValue === 'gemstone') {
                        setShowGemstoneModal(true); // Show modal when "Gemstone" is selected
                      }
                    }}
                  >
                    <option value="">Select Gemstone</option>
                    <option value="gemstone">Gemstone</option> {/* Trigger opening of Gemstone modal */}
                  </Field>

                  {/* Display the selected gemstone name with delete button */}
                  {gemstones.length > 0 && (
                    <div className="gemstone-selected mt-2">
                      {gemstones.map((gemstone, index) => (
                        <div key={index} className="d-flex align-items-center">
                          <span className="badge bg-success me-2">{gemstone.gemstoneName}</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteGemstone(gemstone.gemstoneName, setFieldValue, values)}
                            className="btn btn-link text-danger mx-2"
                          >
                            <FaTrash /> {/* Trash icon */}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Metal and Purity */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formMetal">
                  <Form.Label>Metal</Form.Label>
                  <Field
                    as="select"
                    name="metal"
                    className="form-control" >
                    <option value="">Select metal</option>
                    <option value="gold">Gold</option>
                    <option value="silver">Silver</option>
                    <option value="brass">Brass</option>
                    <option value="copper">Copper</option>
                  </Field>
                  <ErrorMessage name="metal" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formPurity">
                  <Form.Label>Purity</Form.Label>
                  <div className="input-group">
                    <Field
                      type="number"
                      name="purity"
                      className="form-control"
                      min="1"
                      max="100"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                  <ErrorMessage name="purity" component="div" className="text-danger" />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formSize">
                  <Form.Label>Size</Form.Label>
                  <Field type="text" name="size" className="form-control" />
                  <ErrorMessage name="size" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formColor">
                  <Form.Label>Color</Form.Label>
                  <Field type="text" name="color" className="form-control" />
                  <ErrorMessage name="color" component="div" className="text-danger" />
                </Form.Group>
              </Col>
            </Row>

            {/* Weight, Stock Quantity */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formWeight">
                  <Form.Label>Weight</Form.Label>
                  <Field
                    type="text"
                    name="weight"
                    className="form-control" />
                  <ErrorMessage name="weight" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formStockQuantity">
                  <Form.Label>Stock Quantity</Form.Label>
                  <Field
                    type="text"
                    name="stockQuantity"
                    className="form-control" />
                  <ErrorMessage name="stockQuantity" component="div" className="text-danger" />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formPurchasePrice">
                  <Form.Label>Market Price</Form.Label>
                  <Field
                    type="text"
                    name="marketPrice"
                    className="form-control" />
                  <ErrorMessage name="marketPrice" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formPurchasePrice">
                  <Form.Label>Purchase Price</Form.Label>
                  <Field
                    type="text"
                    name="purchasePrice"
                    className="form-control" />
                  <ErrorMessage name="purchasePrice" component="div" className="text-danger" />
                </Form.Group>
              </Col>
            </Row>

            {/* Purchase Price, Selling Price */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formSellingPrice">
                  <Form.Label>Selling Price</Form.Label>
                  <Field
                    type="text"
                    name="sellingPrice"
                    className="form-control" />
                  <ErrorMessage name="sellingPrice" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formStockQuantity">
                  <Form.Label>Origin</Form.Label>
                  <Field
                    type="text"
                    name="origin"
                    className="form-control" />
                  <ErrorMessage name="origin" component="div" className="text-danger" />
                </Form.Group>
              </Col>
            </Row>

            {/* RFID Number */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formPhotos">
                  <Form.Label>Photos</Form.Label>
                  <input
                    type="file"
                    multiple
                    name="photos"
                    accept="image/jpeg, image/png"
                    onChange={(event) => handlePhotoChange(event, setFieldValue, values)}
                    className="form-control"
                  />

                  {/* Show Uploaded Images with Delete Icon */}
                  <div>
                    {values.fileObjects && values.fileObjects.length > 0 && (
                      <div className="image-preview-container">
                        {values.fileObjects.map((file, index) => (
                          <div key={index} className="image-preview-item">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{
                                width: "90px",
                                height: "40px",
                                marginRight: "10px",
                                marginTop: "10px",
                              }}
                            />
                            <span>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                // Remove the image from the array
                                const updatedFileObjects =
                                  values.fileObjects.filter(
                                    (_, i) => i !== index
                                  );
                                const updatedPhotos = values.photos.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue(
                                  "fileObjects",
                                  updatedFileObjects
                                );
                                setFieldValue("photos", updatedPhotos);
                              }}
                              className="btn btn-link text-danger mx-5"
                            >
                              <FaTrash /> {/* Trash icon */}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <ErrorMessage
                    name="photos"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formRfidNo">
                  <Form.Label>RFID Number</Form.Label>
                  <div className="input-group">
                    <Field type="text" name="rfidNo" className="form-control" value={tag} />
                    <Button variant="primary" onClick={() => fetchTagID(setFieldValue)} className="input-group-append">
                      Add
                    </Button>
                  </div>
                  <ErrorMessage
                    name="rfidNo"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Submit Button */}
            <Row>
              <Col md={6}>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Col>
            </Row>

            {/* Gemstone Modal */}
            <Modal show={showGemstoneModal} onHide={() => setShowGemstoneModal(false)} className="custom-modal-dialog">
              <Modal.Header closeButton>
                <Modal.Title>Add Gemstone</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <AddGemstoneForm handleGemstoneSubmit={(gemstoneData) => {
                  const updatedGemstones = [...gemstones, gemstoneData];
                  setGemstones(updatedGemstones);
                  setFieldValue('otheritems', [...values.otheritems, { ...gemstoneData, type: 'gemstone' }]);
                  setShowGemstoneModal(false);
                }} />
              </Modal.Body>
            </Modal>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default WatchForm;